import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Badge, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useBankImportSession,
  useApproveBankImport,
  useRejectAllBankImport,
  usePatchBankImportTransaction,
} from '../hooks/useBankImports';
import { useReconciliationSuggestions, useMergeTransactions } from '../hooks/useReconciliation';
import { useCategories, useCompanies } from '../../../hooks/useFinancialOps';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import type { BankImportTransactionDTO } from '../services/bank-imports.service';

/** Só o que o seletor de rateio precisa. */
type CompanyOption = { id: string; name: string };

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'warning' | 'danger' | 'info'> = {
  PENDING: 'warning',
  IMPORTED: 'accent',
  REJECTED: 'neutral',
  DUPLICATE_SKIPPED: 'neutral',
};
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  IMPORTED: 'Importada',
  REJECTED: 'Rejeitada',
  DUPLICATE_SKIPPED: 'Duplicata ignorada',
};

function SuggestionsCell({
  row,
  companyId,
  sessionId,
  onUserChose,
}: {
  row: BankImportTransactionDTO;
  companyId: string;
  sessionId: string;
  onUserChose: (decision: { row: BankImportTransactionDTO; matchTxIds: string[] } | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useReconciliationSuggestions(
    open
      ? {
          companyId,
          date: row.transactionDate,
          amount: Number(row.amount),
          type: row.type,
        }
      : null
  );
  const matches = data?.data?.matches ?? [];
  const patch = usePatchBankImportTransaction(sessionId);

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Sugestões
      </Button>
    );
  }
  if (isLoading) {
    return <span className="text-xs text-ink-secondary">Buscando…</span>;
  }
  if (!matches.length) {
    return <span className="text-xs text-ink-secondary">Sem sugestões</span>;
  }
  return (
    <div className="space-y-1">
      {matches.slice(0, 1).map((m, idx) => (
        <div key={idx} className="text-xs space-y-1 border rounded p-2">
          <div>
            <strong>Match (net {m.net.toFixed(2)})</strong>
          </div>
          <ul className="list-disc pl-4">
            {m.transactions.map((t) => (
              <li key={t.id}>
                {t.description}: {t.type === 'INCOME' ? '+' : '-'}
                {Number(t.amount).toFixed(2)}
              </li>
            ))}
          </ul>
          <div className="flex gap-1 flex-wrap">
            <Button
              variant="ghost"
              onClick={async () => {
                await patch.mutateAsync({ txId: row.id, patch: { status: 'REJECTED' } });
                setOpen(false);
              }}
            >
              Manter manuais (ignorar OFX)
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                onUserChose({ row, matchTxIds: m.transactions.map((t) => t.id) });
                setOpen(false);
              }}
            >
              Manter OFX (mesclar manuais)
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ImportarExtratoReviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useBankImportSession(id);
  const session = data?.data?.session;
  const transactions: BankImportTransactionDTO[] = data?.data?.transactions ?? [];

  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.data ?? [];
  const { data: companiesData } = useCompanies({ active: true });
  // Rateio é entre duas empresas: a dona do extrato nunca aparece como opção.
  const otherCompanies: CompanyOption[] = (companiesData?.data ?? []).filter(
    (c: CompanyOption) => c.id !== session?.companyId
  );

  const approve = useApproveBankImport(id);
  const rejectAll = useRejectAllBankImport(id);
  const patch = usePatchBankImportTransaction(id);
  const merge = useMergeTransactions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mergeIntents, setMergeIntents] = useState<Map<string, string[]>>(new Map());

  function recordIntent(decision: { row: BankImportTransactionDTO; matchTxIds: string[] } | null) {
    if (!decision) {
      return;
    }
    const next = new Map(mergeIntents);
    next.set(decision.row.id, decision.matchTxIds);
    setMergeIntents(next);
  }

  const pendingTxs = useMemo(
    () => transactions.filter((t) => t.status === 'PENDING'),
    [transactions]
  );

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  }

  async function runApprove(ids: string[], includeDuplicates: boolean) {
    if (ids.length === 0) {
      toast.error('Selecione pelo menos uma transação.');
      return;
    }
    try {
      const r = await approve.mutateAsync({ transactionIds: ids, includeDuplicates });
      const out = r?.data ?? r;
      toast.success(`${out.approved} aprovadas, ${out.skipped} ignoradas`);

      for (const imp of out.imported ?? []) {
        const intent = mergeIntents.get(imp.transactionId);
        if (!intent || intent.length === 0) {
          continue;
        }
        try {
          await merge.mutateAsync({
            survivorId: imp.financialTransactionId,
            mergedIds: intent,
          });
        } catch (mErr: any) {
          toast.error(`Falha ao mesclar ${intent.length} transação(ões): ${mErr?.message ?? ''}`);
        }
      }

      if (out.errors?.length) {
        const rateio = out.errors.filter(
          (e: { code: string }) => e.code === 'INTERCOMPANY_REJECTED'
        ).length;
        toast.error(
          rateio > 0
            ? `${rateio} linha(s) de rateio recusada(s) — a empresa escolhida pode estar inativa. As outras foram importadas.`
            : `${out.errors.length} erro(s) — verifique a lista`
        );
      }
      setSelected(new Set());
      setMergeIntents(new Map());
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao aprovar');
    }
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (isError || !session) {
    return <div className="p-6 text-ink-secondary">Sessão não encontrada.</div>;
  }

  if (session.status === 'FAILED') {
    return (
      <div className="p-6 space-y-2">
        <h1 className="text-xl font-semibold">Importação falhou</h1>
        <p className="text-ink-secondary">{session.errorMessage}</p>
        <Link to="/financeiro?tab=transacoes" className="text-link">
          Voltar
        </Link>
      </div>
    );
  }

  const allPendingIds = pendingTxs.map((t) => t.id);
  const allNonDupIds = pendingTxs.filter((t) => !t.duplicateOfTransactionId).map((t) => t.id);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Revisar importação</h1>
          <p className="text-sm text-ink-secondary">
            {session.fileName} · {session.bankAccount?.bankName} {session.bankAccount?.name} ·{' '}
            {session.periodStart && session.periodEnd
              ? `${formatDateBR(session.periodStart)} a ${formatDateBR(session.periodEnd)}`
              : 'sem período'}
          </p>
        </div>
        <Badge tone={session.status === 'COMPLETED' ? 'accent' : 'warning'}>{session.status}</Badge>
      </header>

      <div className="flex gap-2 flex-wrap">
        <Button
          disabled={!selected.size || approve.isPending}
          onClick={() => runApprove(Array.from(selected), false)}
        >
          Aprovar selecionadas
        </Button>
        <Button
          variant="ghost"
          disabled={!allNonDupIds.length || approve.isPending}
          onClick={() => runApprove(allNonDupIds, false)}
        >
          Aprovar todas (exceto duplicatas)
        </Button>
        <Button
          variant="ghost"
          disabled={!allPendingIds.length || approve.isPending}
          onClick={() => runApprove(allPendingIds, true)}
        >
          Aprovar todas (incluindo duplicatas)
        </Button>
        <Button
          variant="ghost"
          disabled={!allPendingIds.length || rejectAll.isPending}
          onClick={async () => {
            if (!confirm('Rejeitar todas as transações pendentes?')) {
              return;
            }
            await rejectAll.mutateAsync();
            toast.success('Pendentes rejeitadas.');
          }}
        >
          Rejeitar todas
        </Button>
      </div>

      <table className="w-full text-sm">
        <thead className="text-left text-ink-tertiary">
          <tr>
            <th className="py-2"></th>
            <th>Data</th>
            <th>Tipo</th>
            <th className="text-right pr-2">Valor</th>
            <th>Descrição</th>
            <th>Categoria</th>
            <th>Gasto de</th>
            <th>Duplicata?</th>
            <th>Sugestões</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => {
            const isPending = tx.status === 'PENDING';
            const isDup = !!tx.duplicateOfTransactionId;
            return (
              <tr key={tx.id} className={`border-t border-border ${isDup ? 'bg-yellow-50' : ''}`}>
                <td className="py-2">
                  {isPending && (
                    <input
                      type="checkbox"
                      checked={selected.has(tx.id)}
                      onChange={() => toggle(tx.id)}
                    />
                  )}
                </td>
                <td>{formatDateBR(tx.transactionDate)}</td>
                <td>{tx.type === 'INCOME' ? 'Entrada' : 'Saída'}</td>
                <td className="text-right pr-2">{formatCurrency(tx.amount)}</td>
                <td>{tx.description}</td>
                <td>
                  <select
                    disabled={!isPending || patch.isPending}
                    value={tx.suggestedCategoryId ?? ''}
                    onChange={(e) =>
                      patch.mutate({
                        txId: tx.id,
                        patch: { suggestedCategoryId: e.target.value || null },
                      })
                    }
                  >
                    <option value="">—</option>
                    {categories
                      .filter((c: any) => c.type === tx.type)
                      .map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </td>
                <td>
                  {tx.type === 'EXPENSE' && otherCompanies.length > 0 ? (
                    <select
                      disabled={!isPending || patch.isPending}
                      value={tx.beneficiaryCompanyId ?? ''}
                      onChange={(e) =>
                        patch.mutate({
                          txId: tx.id,
                          patch: { beneficiaryCompanyId: e.target.value || null },
                        })
                      }
                      title="Empresa que realmente incorreu na despesa. A despesa vai para a DRE dela; o dinheiro continua saindo desta conta."
                    >
                      <option value="">Esta empresa</option>
                      {otherCompanies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-ink-tertiary">—</span>
                  )}
                </td>
                <td>
                  {isDup && tx.duplicateOfTransaction ? (
                    <span
                      title={`Match: ${tx.duplicateOfTransaction.id} (${tx.duplicateOfTransaction.description})`}
                    >
                      Sim
                    </span>
                  ) : (
                    'Não'
                  )}
                </td>
                <td>
                  <SuggestionsCell
                    row={tx}
                    companyId={session.companyId}
                    sessionId={id}
                    onUserChose={recordIntent}
                  />
                </td>
                <td>
                  <Badge tone={STATUS_TONE[tx.status] ?? 'neutral'}>
                    {STATUS_LABELS[tx.status] ?? tx.status}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
